import React, { useState } from 'react';
import { useTenant } from '../context/TenantContext';

interface CreateTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateTenantModal: React.FC<CreateTenantModalProps> = ({ isOpen, onClose }) => {
  const { createTenant, userRole } = useTenant();
  const [name, setName] = useState('');
  const [tier, setTier] = useState('standard');
  const [primaryContact, setPrimaryContact] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!name.trim()) {
      setError('Tenant name is required.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createTenant(name.trim(), tier, primaryContact.trim() || undefined);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to provision tenant workspace');
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = ['super_admin', 'admin'].includes(userRole.toLowerCase());

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: '#1f2937',
        color: '#f9fafb',
        padding: '24px',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '480px',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Create New Tenant Workspace</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
        </div>

        {!isSuperAdmin ? (
          <div style={{ color: '#ef4444', padding: '12px 0' }}>
            Only Super Admins can provision new tenants.
          </div>
        ) : (
          <form id="create-tenant-form" onSubmit={handleSubmit}>
            {error && (
              <div style={{ backgroundColor: '#7f1d1d', color: '#fca5a5', padding: '8px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '4px', color: '#d1d5db' }}>Tenant Name *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Acme Corp"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  color: '#fff',
                  boxSizing: 'border-box'
                }}
                required
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '4px', color: '#d1d5db' }}>Workspace Tier</label>
              <select
                value={tier}
                onChange={e => setTier(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  color: '#fff',
                  boxSizing: 'border-box'
                }}
              >
                <option value="standard">Standard</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '4px', color: '#d1d5db' }}>Primary Contact Email</label>
              <input
                type="email"
                value={primaryContact}
                onChange={e => setPrimaryContact(e.target.value)}
                placeholder="admin@acme.com"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  color: '#fff',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#4b5563',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const formElement = document.getElementById('create-tenant-form') as HTMLFormElement;
                  if (formElement) formElement.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  else handleSubmit();
                }}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2563eb',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: loading ? 'wait' : 'pointer'
                }}
              >
                {loading ? 'Provisioning...' : 'Provision Tenant'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
